"use client";

import { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  GA4DataLayer,
  GA4DataLayerFn,
  setPreviousPageName,
} from "@packages/lib/utlils/helper-function";
import { DataLayerGA4AttrType, GA4EventAttrType } from "../types/datalayerGA";
import { logClickstreamEvent, ClickstreamInterface } from "./clickstream";
import { setDocumentCookie } from "./cookie-utils";
import { GAConstants } from "@packages/shared-components/services/constants";

interface PageViewLoggingProps {
  userRegion?: any;
  gaData?: GA4EventAttrType;
  csData?: ClickstreamInterface;
  gaoldData?: DataLayerGA4AttrType;
  pageNameLocal: string; //mandatory to set pageName in localStorage
  clearing?: string; //to know the page is clearing/in_year for tracking purpose
  searchViewData?: GA4EventAttrType;
}
const PageViewLogging: React.FC<PageViewLoggingProps> = ({
  userRegion,
  gaData,
  csData,
  gaoldData,
  pageNameLocal,
  clearing,
  searchViewData,
}) => {
  useEffect(() => {
    setDocumentCookie({ name: "userRegion", value: userRegion || "" });
    let dynamicCookie =
      typeof document !== "undefined" &&
      document.cookie.split("userTrackId=")[1]?.split(";")[0];
    if (!dynamicCookie) {
      dynamicCookie = uuidv4().replace(/\D/g, "").slice(0, 10);
      setDocumentCookie({ name: "userTrackId", value: dynamicCookie || "" });
    }
    if (typeof window !== "undefined") {
      // Set current page as previous page for next navigation
      const currentPageName = localStorage.getItem("gaPageName");
      if (currentPageName) {
        localStorage.setItem("previousPageName", currentPageName);
      }

      localStorage.setItem("gaPageName", pageNameLocal ?? "");
      localStorage.setItem("pageGrouping", gaData?.page_grouping ?? "");
      clearing
        ? localStorage.setItem("clearing", clearing ?? "")
        : localStorage.removeItem("clearing");
    }

    //GA event
    if (gaData) {
      const pageViewData = {
        ...gaData,
        event: GAConstants.page_view,
        event_name: GAConstants.page_view,
        cycle_phase: clearing ?? GAConstants.in_year,
      };
      const GAData = async () => GA4DataLayer(pageViewData);
      GAData();
    }
    //For search results page
    if (searchViewData) {
      searchViewData.event = GAConstants.track_event;
      searchViewData.event_name = "search_views";
      searchViewData.cycle_phase = clearing ?? GAConstants.in_year;
      const SearchGAData = async () => GA4DataLayer(searchViewData);
      SearchGAData();
    }
    //GA old event
    if (gaoldData) {
      gaoldData.event = "pageview";
      gaoldData.clearing = clearing ?? GAConstants.in_year;
      const GAData = async () => GA4DataLayerFn(gaoldData);
      GAData();
    }

    //CS event
    if (csData) {
      csData.eventType = csData.eventType ?? "pageViewed";
      csData.functionalityName = csData?.functionalityName ?? "";
      logClickstreamEvent(csData);
    }

    //browser tap switch handling
    const handleTapSwitch = () => {
      if (document.visibilityState === "visible") {
        localStorage.setItem("gaPageName", pageNameLocal ?? "");
        clearing
          ? localStorage.setItem("clearing", clearing ?? "")
          : localStorage.removeItem("clearing");
      }
    };

    //handle page unload to set previous page
    const handleBeforeUnload = () => {
      const currentPageName = localStorage.getItem("gaPageName");
      if (currentPageName) {
        localStorage.setItem("previousPageName", currentPageName);
      }
    };

    //page on load handle
    const handleOnload = () => {
      document.addEventListener("visibilitychange", handleTapSwitch);
      window.addEventListener("beforeunload", handleBeforeUnload);
    };

    //on load, off load logic
    if (document.readyState === "complete") {
      handleOnload();
    } else {
      window.addEventListener("load", handleOnload);
    }

    return () => {
      document.removeEventListener("visibilitychange", handleTapSwitch, true);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("load", handleOnload);
    };
  }, [gaData, csData]);
  return <></>;
};

export default PageViewLogging;
